/**
 * One deliverable chip — a port of the generator's `chipHtml`.
 *
 * Three things here are load-bearing:
 *
 *   1. The CSS class is the CANONICAL state class (s-draft, s-manuscript,
 *      s-proof, s-published) and is never affected by terminology. The 4.5:1
 *      contrast measurements in ONBOARDING.md are keyed to these class names, so
 *      a renamed state must change the word on the chip and nothing else.
 *   2. The state a chip *is* stays the canonical key. Only `terms.state(...)`
 *      supplies the word rendered to the user.
 *   3. Names are NEVER truncated — a stated product requirement. The chip wraps
 *      (`overflow-wrap:break-word` in the ported CSS) and the full name is
 *      repeated in the title attribute for a hover tooltip.
 *
 * An unrecognised state renders as Draft, matching `buildModel`, which also
 * raises a warning about it. The two must agree or the chip and the percentage
 * would tell different stories.
 *
 * **The owner marker (T7.33).** Consultant or Client, from `ownerOf` in
 * @blueprint/shared — blank reads as Consultant, so every chip carries one.
 * It is deliberately NOT a colour: the four states already spend the chip's
 * colour, and a fifth meaning layered on it would be unreadable for anyone who
 * cannot distinguish them. It is a SHAPE at the trailing edge — a filled square
 * for the consultant, a hollow diamond for the client — drawn in `currentColor`,
 * so it inherits the chip ink that is already measured at 4.5:1 against each
 * state in both themes and the state colours themselves are untouched. The word
 * itself is in the tooltip, in the accessible name and in the legend, so nobody
 * has to learn the shapes to read the page.
 */
import { STATE_CLASS, isDeliverableState, ownerOf, type DeliverableRow } from '@blueprint/shared';
import type { KeyboardEvent } from 'react';
import { useTerms } from '../../lib/terms';
import { stateTermKey } from './stateTerms';

/** The rendered word for each canonical owner. Not client-overridable. */
const OWNER_WORD: Record<ReturnType<typeof ownerOf>, string> = {
  consultant: 'Consultant',
  client: 'Client',
};

export function DeliverableChip({
  deliverable,
  highlighted,
  onCycle,
}: {
  deliverable: DeliverableRow;
  /** In the week the highlighter has selected. Adds the outline, nothing else. */
  highlighted: boolean;
  /**
   * Present only where the caller may edit: an editor or admin looking at a
   * draft. Absent — not disabled — otherwise, so a viewer's chip is plain text
   * with no button role, no tab stop and no hint that it could be clicked.
   */
  onCycle?: (index: number) => void;
}) {
  const terms = useTerms();

  const state = isDeliverableState(deliverable.State) ? deliverable.State : 'Draft';
  const label = terms.state(stateTermKey(state));
  const name = String(deliverable.DeliverableName ?? '');

  // Canonical owner key; `OWNER_WORD` is the rendered word. Consultant and
  // Client are NOT among the nine client-overridable display terms (design.md
  // § 2.1), so they are literals here rather than a useTerms() lookup.
  const owner = ownerOf(deliverable);
  const ownerWord = OWNER_WORD[owner];

  const index = deliverable.__i;
  const cycle = onCycle && index !== undefined ? () => onCycle(index) : undefined;

  return (
    <div
      className={`chip ${STATE_CLASS[state]}${highlighted ? ' wk-hl' : ''}`}
      data-di={deliverable.__i}
      title={
        cycle
          ? `${name} — ${label} · ${ownerWord} (click to cycle state)`
          : `${name} — ${label} · ${ownerWord}`
      }
      {...(cycle
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `${name}, ${label}, ${ownerWord}. Press to change state.`,
            onClick: cycle,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                cycle();
              }
            },
          }
        : {})}
    >
      <span className="dot" />
      <span className="t">{name}</span>
      {/* role="img" so the word is announced on a plain chip too, where there is
          no button and therefore no aria-label to carry it. */}
      <span className={`owner own-${owner}`} role="img" aria-label={ownerWord} />
    </div>
  );
}
